import React, { useMemo, useState } from "react";
import _ from "lodash";
import { Modal } from "../../../components/ui/Modal";
import AvatarImage from "../../../assets/images/default_avatar.png";
import { useEventDetailQuery } from "../../../queries/events/useEventDetailQuery";
import { getStatusColor, getAvailableSpots } from "../../../helpers/EventHelpers";
import { getCompanyFormatTime, getCompanyFormatDate } from "../../../helpers/DateUtils";
import { useEventRegistrationsQuery } from "../../../queries/events/useEventRegistrationsQuery";
import { useEventRegistrationMutation } from "../../../queries/events/useEventRegistrationMutation";
import cn from "classnames";

interface EventModalProps {
  event: PublicEvent;
  companyId: number;
  onClose: () => void;
  registrationSuccess: string | null;
  onRegistrationSuccess: (message: string) => void;
}

const initialFormState: RegistrationForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  note: "",
};

export const EventModal: React.FC<EventModalProps> = ({
  event,
  companyId,
  onClose,
  registrationSuccess,
  onRegistrationSuccess,
}) => {
  const registrationMutation = useEventRegistrationMutation();
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<RegistrationForm>(initialFormState);

  const [showParticipants, setShowParticipants] = useState(false);

  const { data: detailData, isLoading: isDetailLoading } = useEventDetailQuery({
    id: event.id,
    companyId,
  });

  const { data: registrationsData, isLoading: isRegistrationsLoading } = useEventRegistrationsQuery(
    {
      eventId: event.id,
      companyId,
    },
  );

  const eventDetail: PublicEventDetail | null = useMemo(
    () => detailData?.data || detailData || null,
    [detailData],
  );

  const registrations: EventRegistration[] = useMemo(
    () => registrationsData?.data || registrationsData || [],
    [registrationsData],
  );

  const displayEvent = eventDetail || event;

  const handleFormChange = (field: keyof RegistrationForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!form.firstName.trim()) return "First name is required";
    if (!form.lastName.trim()) return "Last name is required";
    if (!form.phone.trim() && !form.email.trim()) return "Either phone or email is required";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const handleSubmit = () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    registrationMutation.mutate(
      {
        eventId: event.id,
        companyId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        note: form.note.trim() || undefined,
      },
      {
        onSuccess: (data: any) => {
          if (data.statusCode === 2 && data.data?.success) {
            onRegistrationSuccess(data.message || "You have been successfully registered!");
            setShowForm(false);
          } else {
            setError(data.message || "Registration failed. Please try again.");
          }
        },
        onError: () => setError("An error occurred. Please try again."),
      },
    );
  };

  const spots = eventDetail?.availableSpots ?? getAvailableSpots(event);
  const assignedDoctor = _.head(displayEvent?.clinicianDetails);
  const memberImage = assignedDoctor?.photoUrl || AvatarImage;

  return (
    <Modal show={true} onHide={onClose} title={event.eventName}>
      {!showForm && !showParticipants ? (
        <>
          {displayEvent.headerImageUrl && (
            <div className="events-header-image">
              <img src={displayEvent.headerImageUrl} alt={displayEvent.eventName} />
            </div>
          )}
          <div className="events-modal-body">
            {isDetailLoading && (
              <div className="events-detail-loading">Loading event details...</div>
            )}
            {registrationSuccess && (
              <div className="events-success-message">{registrationSuccess}</div>
            )}
            <div className="events-details-container">
              <div className="events-detail-grid">
                <div className="events-detail">
                  <span className="events-detail-label">Date</span>
                  <span className="events-detail-value">
                    {/*{moment(displayEvent.eventDate).format("dddd, MMMM D, YYYY")}*/}
                    {getCompanyFormatDate(new Date(displayEvent.eventDate))}
                  </span>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Time</span>
                  <div className="d-flex align-items-center">
                    <span className="events-detail-value mr-2 text-uppercase">
                      {getCompanyFormatTime(new Date(displayEvent.startTime))}
                    </span>
                    <span className="events-detail-value text-uppercase">
                      {getCompanyFormatTime(new Date(displayEvent.endTime))}
                    </span>
                  </div>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Status</span>
                  <span
                    className="events-detail-value"
                    style={{ color: getStatusColor(displayEvent) }}
                  >
                    {displayEvent.status?.name || "Planned"}
                  </span>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Type</span>
                  <span className="events-detail-value">
                    {displayEvent.eventType?.name || "General"} (
                    {displayEvent.locationType?.name || "On-site"})
                  </span>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Location</span>
                  <span className="events-detail-value">
                    {displayEvent.location || displayEvent.clinic?.name || "TBA"}
                  </span>
                </div>
              </div>
              <div className="event-clinician">
                <span className="event-clinician-label">Facilitated by</span>
                <span className="event-clinician-label">{assignedDoctor?.name}</span>
                <div className="event-clinician-image">
                  <img src={memberImage} alt={"clinician img"} />
                </div>
              </div>
            </div>
            {displayEvent.description && (
              <div className="events-detail">
                <span className="events-detail-label">Description</span>
                <span className="events-detail-value">{displayEvent.description}</span>
              </div>
            )}
            {eventDetail?.requirements && (
              <div className="events-detail">
                <span className="events-detail-label">Requirements</span>
                <span className="events-detail-value">{eventDetail.requirements}</span>
              </div>
            )}
            {eventDetail?.notes && (
              <div className="events-detail">
                <span className="events-detail-label">Notes</span>
                <span className="events-detail-value">{eventDetail.notes}</span>
              </div>
            )}
            {eventDetail?.zoomLink && (
              <div className="events-detail">
                <span className="events-detail-label">Zoom Link</span>
                <span className="events-detail-value">
                  <a
                    href={eventDetail.zoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="events-detail-link"
                  >
                    {eventDetail.zoomLink}
                  </a>
                </span>
              </div>
            )}
            {eventDetail?.clinic?.address && (
              <div className="events-detail-section">
                <span className="events-detail-section-title">Clinic Information</span>
                <div className="events-detail">
                  <span className="events-detail-label">Clinic</span>
                  <span className="events-detail-value">{eventDetail.clinic.name}</span>
                </div>
                <div className="events-detail">
                  <span className="events-detail-label">Address</span>
                  <span className="events-detail-value">{eventDetail.clinic.address}</span>
                </div>
                {eventDetail.clinic.phone && (
                  <div className="events-detail">
                    <span className="events-detail-label">Phone</span>
                    <span className="events-detail-value">{eventDetail.clinic.phone}</span>
                  </div>
                )}
              </div>
            )}
            {eventDetail?.assignedClinicians && eventDetail.assignedClinicians.length > 0 && (
              <div className="events-detail-section">
                <span className="events-detail-section-title">Clinicians</span>
                <div className="events-clinicians-list">
                  {eventDetail.assignedClinicians.map((clinician) => (
                    <div key={clinician.id} className="events-clinician-item">
                      <span className="events-clinician-name">{clinician.name}</span>
                      <span className="events-clinician-role">
                        {clinician.role}
                        {clinician.isPrimary && (
                          <span className="events-clinician-primary">Primary</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="events-capacity">
              <div className="events-capacity-info">
                <span className="events-capacity-label">Available Spots</span>
                <span className="events-capacity-value">
                  {spots} / {displayEvent.totalCapacity}
                </span>
              </div>
              <div className="events-capacity-bar">
                <div
                  className="events-capacity-fill"
                  style={{
                    width: `${
                      ((displayEvent.totalCapacity - spots) / displayEvent.totalCapacity) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
            {displayEvent.requiresPayment && displayEvent.fee && (
              <div className="events-detail">
                <span className="events-detail-label">Fee</span>
                <span className="events-detail-value">${displayEvent.fee.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="events-modal-footer">
            <button type="button" className="events-modal-button" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowParticipants(true)}
            >
              Participants ({registrations.length})
            </button>
            {!registrationSuccess && (
              <button
                type="button"
                className="events-modal-button primary"
                onClick={() => setShowForm(true)}
                disabled={spots <= 0}
              >
                {spots <= 0 ? "Fully Booked" : "Book Seat"}
              </button>
            )}
          </div>
        </>
      ) : showParticipants ? (
        <>
          <div className="events-modal-body">
            <div className="events-participants-header">
              <span className="events-detail-section-title">
                Registered Participants ({registrations.length})
              </span>
            </div>
            {isRegistrationsLoading && (
              <div className="events-detail-loading">Loading participants...</div>
            )}
            {!isRegistrationsLoading && registrations.length === 0 && (
              <div className="events-participants-empty">No participants registered yet.</div>
            )}
            {registrations.length > 0 && (
              <div className="events-participants-list">
                {registrations.map((reg) => (
                  <div key={reg.id} className="events-participant-card">
                    <div className="events-participant-info">
                      <span className="events-participant-name">
                        {reg.patient.firstName} {reg.patient.lastName}
                      </span>
                      <div className="events-participant-badges">
                        {reg.isPrimaryAttendee && (
                          <span className="events-clinician-primary">Primary</span>
                        )}
                      </div>
                    </div>
                    <div className="events-participant-details">
                      {reg.patient.email && (
                        <span className="events-participant-contact">{reg.patient.email}</span>
                      )}
                      {reg.patient.mobilePhoneNumber && (
                        <span className="events-participant-contact">
                          {reg.patient.mobilePhoneNumber}
                        </span>
                      )}
                    </div>
                    <div className="events-participant-meta">
                      <span className="events-participant-date">
                        Registered: {getCompanyFormatDate(new Date(reg.registrationDate))}
                      </span>
                      <span className="events-participant-type">{reg.registrationType?.name}</span>
                      {reg.relationshipToPrimary && (
                        <span className="events-participant-relationship">
                          ({reg.relationshipToPrimary})
                        </span>
                      )}
                    </div>
                    {reg.notes && <div className="events-participant-notes">{reg.notes}</div>}
                    <div className="events-participant-status">
                      <div className="status-item">
                        <span className="status-item-label">Payment Status</span>
                        <span
                          className={cn("status-item-value pending", {
                            success: reg.paymentCompleted,
                          })}
                        >
                          {reg.paymentCompleted
                            ? `Paid${
                                displayEvent.fee ? ` ($ ${displayEvent?.fee?.toFixed(2)})` : ""
                              }`
                            : "Pending"}
                        </span>
                      </div>
                      <div className="status-item">
                        <span className="status-item-label">Attendance</span>
                        <span
                          className={cn("status-item-value absent", {
                            success: reg.attended,
                          })}
                        >
                          {reg.attended ? "Present" : "Absent"}
                        </span>
                      </div>
                      <div className="status-item">
                        <span className="status-item-label">Consent</span>
                        <span
                          className={cn("status-item-value danger", {
                            success: reg.consentFormSigned,
                          })}
                        >
                          {reg.consentFormSigned ? "Signed" : "Not Signed"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="events-modal-footer">
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowParticipants(false)}
            >
              Back
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="events-modal-body">
            <p className="events-form-description">
              Please fill in your details to register for this event.
            </p>
            {error && <div className="events-error-message">{error}</div>}
            <div className="events-form-group">
              <label className="events-form-label">First Name *</label>
              <input
                className="events-form-input"
                value={form.firstName}
                onChange={(e) => handleFormChange("firstName", e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Last Name *</label>
              <input
                className="events-form-input"
                value={form.lastName}
                onChange={(e) => handleFormChange("lastName", e.target.value)}
                placeholder="Enter your last name"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Phone</label>
              <input
                className="events-form-input"
                value={form.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Email</label>
              <input
                className="events-form-input"
                value={form.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                placeholder="Enter your email address"
                type="email"
              />
            </div>
            <div className="events-form-group">
              <label className="events-form-label">Note (optional)</label>
              <input
                className="events-form-input"
                value={form.note}
                onChange={(e) => handleFormChange("note", e.target.value)}
                placeholder="Any additional information"
              />
            </div>
            <p className="events-form-hint">* Either phone or email is required</p>
          </div>
          <div className="events-modal-footer">
            <button
              type="button"
              className="events-modal-button"
              onClick={() => setShowForm(false)}
            >
              Back
            </button>
            <button
              type="button"
              className="events-modal-button primary"
              onClick={handleSubmit}
              disabled={registrationMutation.isLoading}
            >
              {registrationMutation.isLoading ? "Registering..." : "Register"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};
